import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { bubbleImageSrc, cargoMediaSrc } from '@/lib/bubbleImage';
import { Users, Sparkle, Plus, X, NotePencil, Microphone } from '@phosphor-icons/react';

const KIND_OPTIONS = [
  { id: 'birthday',    label: 'Birthday', emoji: '🎂' },
  { id: 'concert',     label: 'Concert',  emoji: '🎶' },
  { id: 'festival',    label: 'Festival', emoji: '🎪' },
  { id: 'graduation',  label: 'Graduation', emoji: '🎓' },
  { id: 'win',         label: 'Win',      emoji: '🏆' },
  { id: 'wedding',     label: 'Wedding',  emoji: '💍' },
  { id: 'movie_night', label: 'Movie night', emoji: '🍿' },
  { id: 'memorial',    label: 'Memorial', emoji: '🕯️' },
  { id: 'protest',     label: 'Protest',  emoji: '✊' },
  { id: 'open',        label: 'Just a moment', emoji: '✨' },
];

const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const m = (Date.now() - d.getTime()) / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.floor(m)}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

const CreateSwarmModal = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('open');
  const [bubbleId, setBubbleId] = useState('');
  const [duration, setDuration] = useState(24);
  const [bubbles, setBubbles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/bubbles/mine').then(({ data }) => {
      setBubbles(data.bubbles || []);
      if (data.bubbles?.[0]) setBubbleId(data.bubbles[0].id);
    });
  }, [open]);

  const submit = async () => {
    if (!title.trim() || !bubbleId) return;
    setBusy(true);
    try {
      const { data } = await api.post('/swarms',
        { title: title.trim(), kind, bubble_id: bubbleId, duration_hours: duration });
      toast.success('Swarm opened.');
      onCreated?.(data.swarm);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-xl grid place-items-center px-4"
      onClick={onClose}>
      <div className="w-full max-w-md glass rounded-3xl p-6 relative"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60">
          <X size={18} weight="bold" />
        </button>
        <div className="text-center mb-4">
          <Users size={28} weight="duotone" className="text-bx-cyan mx-auto" />
          <div className="text-[10px] uppercase tracking-widest text-bx-cyan mt-2">a group around a moment</div>
          <div className="font-display font-bold text-xl mt-1">Open a swarm</div>
        </div>

        <input
          data-testid="swarm-title"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 60))}
          placeholder="What's the moment?"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-bx-cyan mb-3" />

        <div className="grid grid-cols-5 gap-1.5 mb-3">
          {KIND_OPTIONS.map((k) => (
            <button key={k.id}
              data-testid={`swarm-kind-${k.id}`}
              onClick={() => setKind(k.id)}
              className={`rounded-xl p-2 text-[10px] uppercase tracking-widest font-bold border transition ${
                kind === k.id ? 'border-bx-cyan bg-bx-cyan/10 text-bx-cyan' : 'border-white/10 text-white/65 hover:border-white/30'
              }`}>
              <div className="text-base">{k.emoji}</div>
              <div className="leading-none">{k.label.split(' ')[0]}</div>
            </button>
          ))}
        </div>

        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">your bubble in the swarm</div>
        <select
          data-testid="swarm-bubble"
          value={bubbleId}
          onChange={(e) => setBubbleId(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-bx-cyan mb-3">
          {bubbles.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
        </select>

        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">open for · hours</div>
        <input type="number" min="1" max="168"
          data-testid="swarm-duration"
          value={duration}
          onChange={(e) => setDuration(Math.max(1, Math.min(168, parseInt(e.target.value || '1'))))}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-bx-cyan mb-4" />

        <button
          data-testid="swarm-create"
          onClick={submit} disabled={busy || !title.trim() || !bubbleId}
          className="w-full btn-volt py-3 rounded-full font-bold text-sm">
          {busy ? 'opening…' : 'Open swarm'}
        </button>
      </div>
    </div>
  );
};

const Swarms = () => {
  const [swarms, setSwarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/swarms?open_only=true');
      setSwarms(data.swarms || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-bx-cyan">groups · gatherings · moments</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Swarms</h1>
          <p className="text-white/60 mt-2 text-sm max-w-xl">
            Bubbles cluster around a moment — birthdays, concerts, festivals. Open
            one. Whoever shows up shows up. After it ends, the swarm is sealed
            with a group portrait, hash-chained forever.
          </p>
        </div>
        <button
          data-testid="open-create-swarm"
          onClick={() => setOpenCreate(true)}
          className="btn-volt px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2">
          <Plus size={14} weight="bold" /> Open a swarm
        </button>
      </div>

      {loading ? (
        <div className="text-white/60">listening…</div>
      ) : swarms.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <div className="text-white/65">Nothing gathering yet. Be the first.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {swarms.map((s) => {
            const opt = KIND_OPTIONS.find((k) => k.id === s.kind);
            return (
              <Link key={s.id} to={`/swarm/${s.id}`}
                data-testid={`swarm-card-${s.id}`}
                className="glass rounded-2xl p-5 hover:bg-white/[0.05] transition block">
                <div className="text-3xl mb-2">{opt?.emoji || '✨'}</div>
                <div className="font-display text-lg font-bold leading-snug">{s.title}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
                  {s.kind} · hosted by @{s.host_username}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-bx-cyan">{(s.members || []).length} bubble{(s.members || []).length === 1 ? '' : 's'}</span>
                  <span className="text-white/60">{fmtAgo(s.created_at)}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <CreateSwarmModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={load} />
    </div>
  );
};

export default Swarms;
